import React, { useEffect, useState } from "react";
import { SketchPicker } from "react-color";
import { Wrapper } from "./Panel";
import { ReactComponent as CloseIcon } from "../../.././../../assets/svg/solobuilder/close-icon.svg";
import { guessTextColor } from "../helpers/guessTextColor";
import { useDispatch, useSelector } from "react-redux";
import { onsetClrPicContext } from "../../../../../stateManager/store/common/action";
import { getComponents } from "../helpers";
import { AppState } from "../../../../../stateManager/store";

interface IColorPicker {
    pickerkey: string;
    label: string;
    value: string;
    style?: any;
    open: () => void;
    close: () => void;
    visibility?: boolean;
    width?: string;
}

export const ColorPicker: React.FC<IColorPicker> = (props) => {
    const { pickerkey, label, value, open, close, visibility, width } = props
    const [visible,setVisible] = useState<boolean>(false);
    const { components } = getComponents()
    const { selectedElementId } = useSelector((state: AppState) => state.formJson)
    const component = components[selectedElementId];
    const isCustomSnackBar = (component?.type === "SnackBar" ? component?.properties?.config?.snackType === "Custom" : true) || "";

    useEffect(()=>{!visibility&&setVisible(false)},[visibility])

    return (
        <Wrapper key={pickerkey}>
            <label htmlFor="colorpicker" className="panel-label" style={{width: "fit-content"}} >{label}</label>
            <div className="flex outline-none cursor-pointer"
                data-test-id={`colorpicker-${component?.type}-${label}`}
                style={{
                    alignItems: "center",
                    justifyContent: "center",
                    width: width?width:"170px",
                    borderRadius: "2px",
                    height: "28px",
                    overflow: "hidden",
                    padding: "3px",
                    border: visible?"1px solid #E27841":"1px solid #7D84924B"
                }}>
                <div style={{width: width?"125px":"164px", height: "22px", backgroundColor: value ? value : "white", borderRadius: "2px", display: "flex", alignItems: "center" }}>
                <div
                    id={pickerkey}
                    onClick={(e) => {
                        e.stopPropagation();
                        setVisible(true);
                        if (isCustomSnackBar) {
                            open()
                        }
                    }}
                    onMouseDownCapture={()=>visibility&&close()}
                    key={pickerkey}
                    className="flex justify-center"
                    style={{
                        alignItems: "center",
                        width: "inherit",
                        height: "inherit",
                        lineHeight: "normal",
                        fontWeight: 600,
                        display: "flex",
                        fontSize: "12px",
                        color: value ? guessTextColor(value) : "#969BA5",
                    }}
                >
                    {value ? (value.length === 9 && (value.slice(7, 9).toUpperCase() === 'FF') ? value.slice(0, 7) : value).toUpperCase() : "eg: #000000"}
                </div>
            </div>
        </div>
    </Wrapper>
    )
}

interface IColorPickerMenu {
    close: () => void;
    isCheckedIn: boolean;
    onChange: (key: string, value: string) => void;
    type?: string;
}

export const ColorPickerMenu: React.FC<IColorPickerMenu> = (props: IColorPickerMenu) => {
    const { close, onChange, type } = props;
    const { colorPickerContext } = useSelector((state: any) => state.common)
    const { value, placeholder, where } = colorPickerContext;
    const { dropShadowVisibility, borderVisibility, fontStylesVisibility } = useSelector((state: any) => state.common);
    const dispatch = useDispatch()

    let updatedValue = ''
    if(where.includes('wizardStyle')) {
        let lastValue = where.split('.').pop();
        if(value) {
            if(typeof(value) === 'object') {
                updatedValue = value[lastValue] ? value[lastValue] : ''
            } else {
                updatedValue = value
            }
        }
    } else {
        updatedValue = value
    }

    const defaultColors = [
        { color: '#F5A623', title: '#F5A623' },
        { color: '#FFFFFF', title: '#FFFFFF' },
        { color: '#F8E71C', title: '#F8E71C' },
        { color: '#8B572A', title: '#8B572A' },
        { color: '#7ED321', title: '#7ED321' },
        { color: '#417505', title: '#417505' },
        { color: '#BD10E0', title: '#BD10E0' },
        { color: '#9013FE', title: '#9013FE' },
        { color: '#4A90E2', title: '#4A90E2' },
        { color: '#50E3C2', title: '#50E3C2' },
        { color: '#B8E986', title: '#B8E986' },
        { color: '#000000', title: '#000000' },
        { color: '#4A4A4A', title: '#4A4A4A' },
        { color: '#9B9B9B', title: '#9B9B9B' },
        { color: '#00000000', title: 'Transparent' },
    ]

    function hexToRgba(hex: any) {
        hex = hex && hex?.replace('#', '');

        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        const a = parseInt(hex.substring(6, 8), 16);

        const normalizedR = r
        const normalizedG = g
        const normalizedB = b
        const normalizedA = a ? a / 255 : 1

        const rgba = `rgba(${normalizedR}, ${normalizedG}, ${normalizedB}, ${normalizedA})`;

        return rgba;
    }

    function rgbaToHex(rgba: any) {
        const rgbValues = rgba?.substring(rgba.indexOf('(') + 1, rgba.lastIndexOf(')')).split(',');
        const r = parseInt(rgbValues[0].trim(), 10).toString(16).padStart(2, '0');
        const g = parseInt(rgbValues[1].trim(), 10).toString(16).padStart(2, '0');
        const b = parseInt(rgbValues[2].trim(), 10).toString(16).padStart(2, '0');
        const alpha = Math.round(parseFloat(rgbValues[3].trim()) * 255).toString(16).padStart(2, '0');  
        const hex = alpha === 'ff' ? `#${r}${g}${b}` : `#${r}${g}${b}${alpha}`;
        return hex;
    }

    useEffect (() => {
        document.addEventListener('keydown', detectKeyDown)
        return () => {
            document.removeEventListener('keydown', detectKeyDown);
        }
    }, [])

    const detectKeyDown = (e:KeyboardEvent) => {
        if (e.key === "Escape") {
            close();
        }
    }

    useEffect(() => {
        const ele: any = document.querySelector('[title="Transparent"]');
        if (ele && ele.style) {
            ele.style.backgroundColor = "#dee2e6";
            ele.style.backgroundImage = "linear-gradient(45deg, rgb(247, 247, 247) 25%, transparent 25%), linear-gradient(-45deg, rgb(247, 247, 247) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, rgb(247, 247, 247) 75%), linear-gradient(-45deg, transparent 75%, rgb(247, 247, 247) 75%)";
            ele.style.backgroundSize = "10px 10px";
            ele.style.backgroundPosition = "0px 0px, 0px 10px, 10px -10px, -10px 0px";
        }
    }, [])
    return (
        <div className="custom-color" data-test-id={`custom-color-${type}`} style={{ right: (dropShadowVisibility || borderVisibility || fontStylesVisibility) ? "540px" : "300px", bottom: 10 }} onClick={(e) => e.stopPropagation()}>
            <div className="w-full h-10 flex justify-between items-center text-xs font-semibold "
                style={{
                    background: "#EFF0F4",
                }}
            >
                <div className="ml-2.5" style={{ fontSize: "12px", fontWeight: 600 }}>Color picker</div>
                <CloseIcon
                    data-test-id="close-colorpicker"
                    className="mr-2.5 cursor-pointer"
                    onClick={() => close()}
                ></CloseIcon>
            </div>
            <SketchPicker
                color={updatedValue ? hexToRgba(updatedValue) : "#FFFFFFF"}
                onChange={(value) => {
                    const { rgb = {} } = value || {}
                    const { r, g, b, a }: any = rgb || {}
                    const rgba = `rgba(${r}, ${g}, ${b}, ${a})`;
                    const hex = rgbaToHex(rgba);
                    onChange(where, hex);
                    dispatch(onsetClrPicContext({
                        ...colorPickerContext, value: hex
                    }))
                }}
                presetColors={defaultColors}
            />
            <div className="flex flex-row items-center justify-between" style={{padding:"0px 10px"}}>
                <div className="mr-4" style={{ fontWeight: "bold" }}>Hex</div>
                <input
                    id={where}
                    className="panel-input"
                    onChange={(e: any) => {
                        let value = e.target.value;
                        let pattern = /#|[a-f]|[A-F]|[0-9]/g;
                        let result = value.match(pattern);
                        if (result) {
                            const hex = result.join("");
                            if (/^#/.test(hex) && hex.split('#').length - 1 <= 1 && hex.length <= 9) {
                                onChange(where, hex);
                                dispatch(onsetClrPicContext({
                                    ...colorPickerContext, value: value
                                }))
                            }
                        } else {
                            onChange(where, "");
                            dispatch(onsetClrPicContext({
                                ...colorPickerContext, value: ""
                            }))
                        }
                    }}
                    type="text"
                    data-test-id="hexaValue_inputValue"
                    value={updatedValue ? (updatedValue.length === 9 && (updatedValue.slice(7, 9).toUpperCase() === 'FF' || updatedValue.slice(7, 9).toUpperCase() === '00') ? updatedValue.slice(0, 7) : updatedValue).toUpperCase() : ""}
                    placeholder={placeholder}
                />
            </div>
        </div>
    )
}